import { Box, Divider, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { useGetOne, useRecordContext, useTranslate } from 'react-admin';

import { doesCampaignTypeAcceptPayout } from '@Common/bo/b2b_campaigns';
import { B2bCampaignType, GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import { GetCampaignsDashboardAmountsIdEndpoint } from '@Common/types/bo/b2b/campaigns/dashboard';
import { B2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts';

import { ShowSection } from '@sh4res/bo-common/components';
import {
  LegendLabel,
  PieCenterLabel,
} from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';
import { TranslationKeys } from '@sh4res/bo-common/i18n/types';
import { formatCentsNumber } from '@sh4res/bo-common/utils';

const CATEGORY_CONFIG: Record<
  'pei' | 'percoi' | 'er',
  { color: string; legendKey: TranslationKeys }
> = {
  pei: {
    color: '#02CB59',
    legendKey: 'resources.bo_common.b2b.campaigns.dashboard.total_amounts.legend.pei',
  },
  percoi: {
    color: '#FF5FCB',
    legendKey: 'resources.bo_common.b2b.campaigns.dashboard.total_amounts.legend.percoi',
  },
  er: {
    color: '#FFe45F',
    legendKey: 'resources.bo_common.b2b.campaigns.dashboard.total_amounts.legend.er',
  },
};

/**
 * Builds the inner-ring slices of the "Total Amounts" chart: one slice per plan, labelled by the
 * plan label (falling back to the plan id when absent) and prefixed with the plan type so each
 * slice still shows which type the plan belongs to. The two shares plans (Libre / Piloté) of a
 * single labelled product are grouped together so they are not shown as duplicates.
 */
function buildInnerSlices({
  data,
  translate,
}: {
  data: GetCampaignsDashboardAmountsIdEndpoint['Client']['ResponseBody'];
  translate: (key: string) => string;
}): { id: string; value: number; label: string; color: string }[] {
  const groups = new Map<
    string,
    { category: 'pei' | 'percoi' | 'er'; label: string; value: number }
  >();

  data.total_amounts.invested_per_plan.forEach((plan) => {
    const hasLabel = plan.plan_label !== null && plan.plan_label.length > 0;
    const groupKey = `${plan.category}:${hasLabel ? plan.plan_label : `#${plan.plan_id}`}`;
    const typeLabel = translate(CATEGORY_CONFIG[plan.category].legendKey);
    const label = hasLabel
      ? `${typeLabel} - (${plan.plan_label})`
      : `${typeLabel} - #${plan.plan_id}`;
    const existing = groups.get(groupKey) ?? { category: plan.category, label, value: 0 };
    existing.value += plan.amount;
    groups.set(groupKey, existing);
  });

  const perCategoryIndex: Record<'pei' | 'percoi' | 'er', number> = { pei: 0, percoi: 0, er: 0 };

  return Array.from(groups.entries()).map(([groupKey, group]) => {
    const index = perCategoryIndex[group.category];
    perCategoryIndex[group.category] = index + 1;
    return {
      id: groupKey,
      value: group.value,
      label: group.label,
      color: CATEGORY_CONFIG[group.category].color,
    };
  });
}

function createPieData({
  data,
  campaignType,
  choiceCollectedBy,
  pieType,
  translate,
}: {
  data: GetCampaignsDashboardAmountsIdEndpoint['Client']['ResponseBody'];
  campaignType: B2bCampaignType | undefined;
  choiceCollectedBy: B2bCampaignActionHandler | null | undefined;
  pieType: 'payed_out' | 'outer' | 'inner';
  translate: (key: string) => string;
}) {
  const pieData: { id: string; value: number; label: string; color: string }[] = [];

  const totalInvestedAmount =
    data.total_amounts.invested_pei_amount +
    data.total_amounts.invested_percoi_amount +
    data.total_amounts.invested_er_amount;

  if (doesCampaignTypeAcceptPayout(campaignType)) {
    pieData.push({
      id: 'payed_out',
      value: data.total_amounts.payed_out_amount,
      label: translate(
        'resources.bo_common.b2b.campaigns.dashboard.total_amounts.legend.payed_out',
      ),
      color: pieType === 'payed_out' ? '#594FF4' : 'rgba(0,0,0,0)',
    });
  }

  if (pieType === 'inner') {
    pieData.push(...buildInnerSlices({ data, translate }));
  } else {
    pieData.push({
      id: 'invested_per_plan_category',
      value: totalInvestedAmount,
      label: translate(
        'resources.bo_common.b2b.campaigns.dashboard.total_amounts.legend.invested_per_plan_category',
      ),
      color: pieType !== 'payed_out' ? 'rgba(89, 79, 244, 0.2)' : 'rgba(0,0,0,0)',
    });
  }

  if (choiceCollectedBy === 'distributor') {
    const choiceUnknownAmount =
      data.total_amounts.total_potential_amount -
      (data.total_amounts.payed_out_amount + totalInvestedAmount);

    pieData.push({
      id: 'choice_unknown',
      value: choiceUnknownAmount,
      label: translate(
        'resources.bo_common.b2b.campaigns.dashboard.total_amounts.legend.choice_unknown',
      ),
      color: pieType === 'payed_out' ? '#B2ADFC' : 'rgba(0,0,0,0)',
    });
  }

  return pieData;
}

export function AmountsSection({ resource, id }: { id: number; resource: string }) {
  const translate = useTranslate();
  const record = useRecordContext<GetB2bCampaignOneResponse>();

  const { data } = useGetOne<GetCampaignsDashboardAmountsIdEndpoint['Client']['ResponseBody']>(
    `${resource}/dashboard/amounts`,
    { id },
  );

  if (!data) {
    return null;
  }

  const pieDataPayedOut = createPieData({
    data,
    campaignType: record?.type,
    choiceCollectedBy: record?.choice_collected_by,
    pieType: 'payed_out',
    translate: (key: string) => translate(key),
  });

  const pieDataOuter = createPieData({
    data,
    campaignType: record?.type,
    choiceCollectedBy: record?.choice_collected_by,
    pieType: 'outer',
    translate: (key: string) => translate(key),
  });

  const pieDataInner = createPieData({
    data,
    campaignType: record?.type,
    choiceCollectedBy: record?.choice_collected_by,
    pieType: 'inner',
    translate: (key: string) => translate(key),
  });

  const isCampaignHasPayout = doesCampaignTypeAcceptPayout(record?.type);

  const indexConsideringPayedOut = isCampaignHasPayout ? 1 : 0;
  const indexWithoutChoiceUnknown =
    record?.choice_collected_by === 'distributor' ? pieDataInner.length - 1 : pieDataInner.length;

  return (
    <>
      <ShowSection label="resources.bo_common.b2b.campaigns.dashboard.total_amounts.label">
        <Divider />
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ paddingLeft: '60px' }}>
            <Typography>
              {translate('resources.bo_common.b2b.campaigns.dashboard.total_amounts.description')}
            </Typography>
            <PieChart
              sx={{
                '& .MuiPieArc-root': {
                  strokeWidth: 0,
                },
              }}
              series={[
                {
                  data: pieDataPayedOut,
                  innerRadius: 134,
                  outerRadius: 150,
                  paddingAngle: 1,
                  cornerRadius: 3,
                  valueFormatter: (entry) =>
                    formatAnswerRateValue(entry.value, data.total_amounts.total_potential_amount),
                },
                {
                  data: pieDataOuter,
                  innerRadius: 144,
                  outerRadius: 150,
                  paddingAngle: 1,
                  cornerRadius: 4,
                  valueFormatter: (entry) =>
                    formatAnswerRateValue(entry.value, data.total_amounts.total_potential_amount),
                },
                {
                  data: pieDataInner,
                  innerRadius: 134,
                  outerRadius: 140,
                  paddingAngle: 1,
                  cornerRadius: 4,
                  valueFormatter: (entry) =>
                    formatAnswerRateValue(entry.value, data.total_amounts.total_potential_amount),
                },
              ]}
              height={350}
              width={400}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}>
              <PieCenterLabel
                total={formatCentsNumber(data.total_amounts.total_potential_amount)}
                label={
                  'resources.bo_common.b2b.campaigns.dashboard.total_amounts.chart.center_label'
                }
              />
            </PieChart>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}>
            {isCampaignHasPayout && (
              <LegendLabel
                id={pieDataPayedOut[0].id}
                color={pieDataPayedOut[0].color}
                label={pieDataPayedOut[0].label}
                value={formatAnswerRateValue(
                  pieDataPayedOut[0].value,
                  data.total_amounts.total_potential_amount,
                )}
              />
            )}
            <LegendLabel
              id={pieDataOuter[indexConsideringPayedOut].id}
              color={pieDataOuter[indexConsideringPayedOut].color}
              label={pieDataOuter[indexConsideringPayedOut].label}
              value={formatAnswerRateValue(
                pieDataOuter[indexConsideringPayedOut].value,
                data.total_amounts.total_potential_amount,
              )}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                marginLeft: 4,
              }}>
              {pieDataInner
                .slice(indexConsideringPayedOut, indexWithoutChoiceUnknown)
                .map((entry, idx) => (
                  <LegendLabel
                    key={idx}
                    id={String(idx)}
                    color={entry.color}
                    label={entry.label}
                    value={formatAnswerRateValue(
                      entry.value,
                      pieDataOuter[indexConsideringPayedOut].value,
                    )}
                  />
                ))}
            </Box>
            {record?.choice_collected_by === 'distributor' && isCampaignHasPayout && (
              <LegendLabel
                id={pieDataPayedOut[2].id}
                color={pieDataPayedOut[2].color}
                label={pieDataPayedOut[2].label}
                value={formatAnswerRateValue(
                  pieDataPayedOut[2].value,
                  data.total_amounts.total_potential_amount,
                )}
              />
            )}
          </Box>
        </Box>
      </ShowSection>
    </>
  );
}

function formatAnswerRateValue(value: number, total: number) {
  const percentage = total === 0 ? '' : `(${Math.round((value / total) * 100)}%)`;
  return `${formatCentsNumber(value)} ${percentage}`;
}
